import { z } from 'zod';
import { rechargeRunStatusSchema } from '../status/recharge.js';
import { existingDiscountRuleSchema, renewalDiscountApplicationStatusSchema, renewalDiscountCalculationSchema, renewalDiscountFeatureModeSchema, renewalDiscountRunKindSchema, renewalDiscountSourceSchema, renewalDiscountStateSchema, } from '../status/renewal-discount.js';
import { decimalStringSchema, isoDateTimeSchema, isoDaySchema, objectIdSchema, paginationQuerySchema } from './common.js';
/**
 * Opération « remise sur le prochain renouvellement » — `docs/specs-v2/07-remise-renouvellement.md`.
 * Montants en chaînes décimales (`12.34`), en euros.
 */
/** Palier : à partir de `minProducts` produits dans le renouvellement, `percent` % de remise (`07` §3.1). */
export const renewalDiscountTierSchema = z.object({
    minProducts: z.number().int().min(1).max(100),
    percent: z.number().min(1).max(100),
});
export const renewalDiscountTiersSchema = z
    .array(renewalDiscountTierSchema)
    .min(1)
    .max(20)
    .refine((tiers) => new Set(tiers.map((tier) => tier.minProducts)).size === tiers.length, {
    message: 'Deux paliers ne peuvent pas avoir le même nombre de produits.',
});
export const renewalDiscountConfigInputSchema = z.object({
    /** `false` (défaut) : seuls les clients déjà abonnés à l'activation (`07` §2.1). */
    includeNewSubscribers: z.boolean().default(false),
    /**
     * Ignorer les abonnements legacy (`07` §2.1) : non migrés, ou migrés au palier
     * legacy (`_legacy_tier`). Leurs produits ne comptent ni pour le palier ni
     * pour le montant de la remise.
     */
    excludeLegacy: z.boolean().default(false),
    /** Le renouvellement doit contenir au moins un de ces SKU (composants de bundle compris). Vide = tous. */
    productSkus: z.array(z.string().trim().min(1)).default([]),
    calculation: renewalDiscountCalculationSchema,
    tiers: renewalDiscountTiersSchema,
    existingDiscountRule: existingDiscountRuleSchema.default('ignore'),
});
/** Période des renouvellements visés, en jours parisiens inclus (`AAAA-MM-JJ`). */
export const renewalDiscountPeriodInputSchema = z
    .object({ start: isoDaySchema, end: isoDaySchema })
    .refine((period) => period.start <= period.end, { message: 'La fin de période doit suivre le début.', path: ['end'] });
/** `POST /api/operations` et `PUT /api/operations/:id` pour une remise renouvellement. */
export const renewalDiscountOperationInputSchema = z.object({
    kind: z.literal('renewal_discount'),
    name: z.string().trim().min(1),
    period: renewalDiscountPeriodInputSchema,
    renewalDiscount: renewalDiscountConfigInputSchema,
});
export const renewalDiscountCountsSchema = z.partialRecord(renewalDiscountApplicationStatusSchema, z.number().int().min(0));
/** Ligne d'aperçu : un client et le renouvellement retenu. */
export const renewalDiscountPreviewRowSchema = z.object({
    customerId: z.string(),
    email: z.string().nullable(),
    chargeId: z.string().nullable(),
    scheduledAt: isoDaySchema.nullable(),
    status: renewalDiscountApplicationStatusSchema,
    productCount: z.number().int().min(0),
    percent: z.number().nullable(),
    amountBefore: decimalStringSchema.nullable(),
    discountAmount: decimalStringSchema.nullable(),
    existingCode: z.string().nullable(),
});
/** Résultat de l'aperçu (`07` §6.2), conservé sur le run. */
export const renewalDiscountPreviewSchema = z.object({
    /** Clients avec au moins un renouvellement déjà généré dans la période. */
    customersWithCharge: z.number().int().min(0),
    byStatus: renewalDiscountCountsSchema,
    byTier: z.array(z.object({ minProducts: z.number().int(), percent: z.number(), customers: z.number().int().min(0) })),
    totalBefore: decimalStringSchema,
    totalDiscount: decimalStringSchema,
    /**
     * Clients abonnés dont le renouvellement dans la période n'existe pas encore
     * chez Recharge (prochaine charge avant le début de la période) : traités au
     * fil de l'eau par le webhook `charge/created` et la réconciliation.
     */
    notGeneratedCustomers: z.number().int().min(0),
    /** Les 200 premières lignes, par date de renouvellement. */
    rows: z.array(renewalDiscountPreviewRowSchema),
});
export const renewalDiscountRunSchema = z.object({
    id: objectIdSchema,
    kind: renewalDiscountRunKindSchema,
    status: rechargeRunStatusSchema,
    startedAt: isoDateTimeSchema,
    finishedAt: isoDateTimeSchema.nullable(),
    counts: z.record(z.string(), z.number()),
    error: z.string().nullable(),
    preview: renewalDiscountPreviewSchema.nullable(),
});
/** Bloc propre aux remises renouvellement dans le détail d'une opération. */
export const renewalDiscountDetailSchema = z.object({
    ...renewalDiscountConfigInputSchema.shape,
    periodDays: z.object({ start: isoDaySchema, end: isoDaySchema }),
    state: renewalDiscountStateSchema,
    activatedAt: isoDateTimeSchema.nullable(),
    /** Préfixe des codes Recharge générés (un code par renouvellement). */
    codePrefix: z.string().nullable(),
    /** Mode effectif de la fonction Recharge `renewalDiscount` : `disabled` aussi en mode test global. */
    featureMode: renewalDiscountFeatureModeSchema,
    /** Pourquoi la fonction n'écrit pas chez Recharge (désactivée, mode test), `null` si elle est active. */
    unavailableReason: z.string().nullable(),
    counts: renewalDiscountCountsSchema,
    totalDiscount: decimalStringSchema,
    lastRuns: z.array(renewalDiscountRunSchema),
});
/** `POST /api/operations/:id/renewal-discount/activate`. */
export const renewalDiscountActivateBodySchema = z.object({}).strict();
/** `POST /api/operations/:id/renewal-discount/preview-customer`. */
export const renewalDiscountCustomerPreviewBodySchema = z.object({
    /** Email ou identifiant client Recharge. */
    query: z.string().trim().min(1).max(320),
});
export const renewalDiscountPreviewLineSchema = z.object({
    title: z.string(),
    sku: z.string().nullable(),
    quantity: z.number().int(),
    unitPrice: decimalStringSchema,
    /** Prix Shopify du variant (avant remise abonnement), `null` s'il est introuvable. */
    shopifyUnitPrice: decimalStringSchema.nullable(),
    /** Bundle Recharge de la ligne (`_rc_bundle`) : les composants d'un bundle sont des lignes distinctes. */
    bundleId: z.string().nullable(),
    /** Ligne d'un abonnement legacy (non migré ou migré au palier legacy). */
    legacy: z.boolean(),
});
export const renewalDiscountCustomerChargeSchema = z.object({
    chargeId: z.string(),
    scheduledAt: isoDaySchema,
    inPeriod: z.boolean(),
    existingCode: z.string().nullable(),
    lines: z.array(renewalDiscountPreviewLineSchema),
});
export const renewalDiscountCustomerPreviewSchema = z.object({
    customerId: z.string(),
    email: z.string().nullable(),
    existingSubscriber: z.boolean(),
    charges: z.array(renewalDiscountCustomerChargeSchema),
    decision: z.object({
        status: renewalDiscountApplicationStatusSchema.nullable(),
        /** Explication en une phrase. */
        reason: z.string(),
        chargeId: z.string().nullable(),
        productCount: z.number().int().min(0),
        percent: z.number().nullable(),
        amountBefore: decimalStringSchema.nullable(),
        targetAmount: decimalStringSchema.nullable(),
        discountAmount: decimalStringSchema.nullable(),
        amountAfter: decimalStringSchema.nullable(),
    }),
    /** Renouvellement pas encore généré : date prévue par `delivery_schedule`, si elle tombe dans la période. */
    projectedDate: isoDaySchema.nullable(),
});
export const renewalDiscountHistoryEntrySchema = z.object({
    at: isoDateTimeSchema,
    action: z.string(),
    chargeId: z.string().nullable(),
    detail: z.string().nullable(),
});
export const renewalDiscountApplicationListItemSchema = z.object({
    id: objectIdSchema,
    customerId: z.string(),
    email: z.string().nullable(),
    chargeId: z.string().nullable(),
    scheduledAt: isoDaySchema.nullable(),
    status: renewalDiscountApplicationStatusSchema,
    source: renewalDiscountSourceSchema,
    productCount: z.number().int().min(0),
    percent: z.number().nullable(),
    amountBefore: decimalStringSchema.nullable(),
    discountAmount: decimalStringSchema.nullable(),
    discountCode: z.string().nullable(),
    previousCode: z.string().nullable(),
    error: z.string().nullable(),
    updatedAt: isoDateTimeSchema,
});
export const renewalDiscountApplicationDetailSchema = renewalDiscountApplicationListItemSchema.extend({
    history: z.array(renewalDiscountHistoryEntrySchema),
    targetAmount: decimalStringSchema.nullable(),
});
export const renewalDiscountApplicationListQuerySchema = z.object({
    ...paginationQuerySchema.shape,
    status: renewalDiscountApplicationStatusSchema.optional(),
    search: z.string().trim().min(1).optional(),
});
//# sourceMappingURL=renewal-discount.js.map